import React, { useState, useEffect } from 'react'
import { hsclLogo2, agoraCoreNew } from '../../assets/images'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useValidateResetToken, useResetPasswordConfirm } from '../../hooks/usePasswordReset'
import {
    IconEye,
    IconEyeOff,
    IconChevronLeft,
    IconUsersGroup,
    IconClock,
    IconChartBar,
    IconCreditCard
} from '@tabler/icons-react'

export default function ResetPassword() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const token = searchParams.get('token')

    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showNew, setShowNew] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [fieldErrors, setFieldErrors] = useState({})
    const [isLoaded, setIsLoaded] = useState(false)

    const { data: tokenState, isLoading: validating } = useValidateResetToken(token)
    const { mutate: resetConfirm, isPending: loading } = useResetPasswordConfirm()

    useEffect(() => {
        setIsLoaded(true)
    }, [])

    const handleSubmit = (e) => {
        e.preventDefault()
        setFieldErrors({})

        if (newPassword !== confirmPassword) {
            setFieldErrors({ confirm_password: 'Passwords do not match.' })
            return
        }

        resetConfirm(
            { token, new_password: newPassword, confirm_password: confirmPassword },
            {
                onSuccess: () => {
                    toast.success('Password reset successfully. You can now log in.')
                    navigate('/auth-login')
                },
                onError: (error) => {
                    const data = error.response?.data
                    if (data) {
                        const errors = {}
                        if (data.confirm_password) errors.confirm_password = Array.isArray(data.confirm_password) ? data.confirm_password[0] : data.confirm_password
                        if (data.token) errors.token = Array.isArray(data.token) ? data.token[0] : data.token
                        if (data.new_password) errors.new_password = Array.isArray(data.new_password) ? data.new_password[0] : data.new_password
                        if (Object.keys(errors).length > 0) {
                            setFieldErrors(errors)
                        } else {
                            toast.error(data.detail || data.message || 'Something went wrong. Please try again.')
                        }
                    } else {
                        toast.error('Something went wrong. Please try again.')
                    }
                },
            }
        )
    }

    const leftPanel = (
        <div className='hidden lg:flex lg:w-[40%] flex-col bg-gradient-to-br from-[#011a4d] via-[#012E80] to-[#01245f] p-16 xl:p-24 text-white relative overflow-hidden'>
            <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-white/10 rounded-full blur-[100px] animate-pulse-slow"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#012E80]/30 rounded-full blur-[80px]"></div>

            <div className={`mb-20 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                <img src={agoraCoreNew} alt='AGORA' className='h-24 w-auto object-contain opacity-90 brightness-110' />
            </div>

            <div className='flex-1 flex flex-col justify-center relative z-10'>
                <h2 className={`text-4xl xl:text-5xl font-bold mb-16 tracking-tight transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    Secure your account
                </h2>
                <div className='space-y-10'>
                    {[
                        { icon: IconUsersGroup, title: 'Comprehensive HR Management', desc: 'Streamline employee data, departments, and attendance tracking in one central hub.' },
                        { icon: IconClock, title: 'Leave & Time Tracking', desc: 'Effortlessly manage leave requests, balances, and detailed timesheets for your workforce.' },
                        { icon: IconChartBar, title: 'Performance & KPI Insights', desc: 'Monitor organizational growth with intuitive KPI dashboards and performance history tracking.' },
                        { icon: IconCreditCard, title: 'Financial & Accounts Control', desc: 'Stay on top of your bottom line with integrated management of payments, expenses, and invoices.' }
                    ].map((item, idx) => (
                        <div
                            key={idx}
                            className={`flex items-start gap-6 group transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                            style={{ transitionDelay: `${100 + (idx * 150)}ms` }}
                        >
                            <div className='p-3.5 bg-white/10 rounded-2xl group-hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/5'>
                                <item.icon size={26} className='text-white' />
                            </div>
                            <div className='pt-1'>
                                <h3 className='text-lg xl:text-xl font-bold mb-1.5'>{item.title}</h3>
                                <p className='text-white/70 text-sm leading-relaxed max-w-sm font-light'>{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )

    return (
        <div className='flex h-svh overflow-hidden bg-white'>
            {leftPanel}

            <div className='w-full lg:w-[60%] flex flex-col bg-white overflow-y-auto px-8 md:px-16 lg:px-24 py-12'>
                <div className={`flex-1 flex items-center justify-center py-20 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className='w-full max-w-[460px]'>

                        {/* Validating */}
                        {validating && (
                            <div className='text-center'>
                                <div className='w-10 h-10 border-2 border-[#012E80]/20 border-t-[#012E80] rounded-full animate-spin mx-auto mb-6'></div>
                                <p className='text-gray-500 font-light'>Validating your reset link...</p>
                            </div>
                        )}

                        {/* Invalid token */}
                        {!validating && (!token || (tokenState && !tokenState.valid)) && (
                            <div className='text-center'>
                                <div className='text-center mb-14'>
                                    <h1 className='text-4xl font-black text-gray-900 mb-3 tracking-tight'>Link Expired</h1>
                                    <p className='text-gray-500 font-light'>This reset link is no longer valid</p>
                                </div>
                                <div className='mb-10 p-6 bg-red-50 rounded-2xl border border-red-100'>
                                    <p className='text-red-700 leading-relaxed text-sm'>
                                        {!token ? 'No reset token found in the link.' : (tokenState?.error || 'This reset link has expired or already been used.')}
                                    </p>
                                </div>
                                <Link
                                    to='/auth-forgot-password'
                                    className='inline-block w-full py-4 bg-[#012E80] hover:bg-[#011a4d] text-white text-lg font-bold rounded-xl transition-all duration-300 text-center shadow-lg shadow-[#012E80]/20'
                                >
                                    Request new link
                                </Link>
                                <div className='text-center mt-8'>
                                    <Link to='/auth-login' className='inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#012E80] transition-colors group'>
                                        <IconChevronLeft size={18} className='group-hover:-translate-x-1 transition-transform' />
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        )}

                        {/* Valid token — reset form */}
                        {!validating && tokenState?.valid && (
                            <>
                                <div className='text-center mb-14'>
                                    <h1 className='text-4xl font-black text-gray-900 mb-3 tracking-tight'>New Password</h1>
                                    <p className='text-gray-500 font-light'>
                                        Resetting for: <span className='font-semibold text-gray-800'>{tokenState.email}</span>
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className='flex flex-col gap-8'>
                                    <div className='flex flex-col gap-2'>
                                        <label className='text-sm font-semibold text-gray-700 ml-1'>New Password</label>
                                        <div className='relative'>
                                            <input
                                                type={showNew ? 'text' : 'password'}
                                                placeholder='Enter new password'
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                                className={`w-full px-5 py-4 rounded-xl border ${fieldErrors.new_password ? 'border-red-400' : 'border-gray-200'} focus:border-[#012E81] focus:ring-2 focus:ring-[#012E81]/20 transition-all outline-none text-gray-800 pr-14`}
                                            />
                                            <button type='button' onClick={() => setShowNew(!showNew)} tabIndex={-1}
                                                className='absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors'>
                                                {showNew ? <IconEyeOff size={22} /> : <IconEye size={22} />}
                                            </button>
                                        </div>
                                        {fieldErrors.new_password && <p className='text-red-500 text-xs ml-1'>{fieldErrors.new_password}</p>}
                                    </div>

                                    <div className='flex flex-col gap-2'>
                                        <label className='text-sm font-semibold text-gray-700 ml-1'>Confirm Password</label>
                                        <div className='relative'>
                                            <input
                                                type={showConfirm ? 'text' : 'password'}
                                                placeholder='Confirm new password'
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                disabled={loading}
                                                required
                                                className={`w-full px-5 py-4 rounded-xl border ${fieldErrors.confirm_password ? 'border-red-400' : 'border-gray-200'} focus:border-[#012E81] focus:ring-2 focus:ring-[#012E81]/20 transition-all outline-none text-gray-800 pr-14`}
                                            />
                                            <button type='button' onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}
                                                className='absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors'>
                                                {showConfirm ? <IconEyeOff size={22} /> : <IconEye size={22} />}
                                            </button>
                                        </div>
                                        {fieldErrors.confirm_password && <p className='text-red-500 text-xs ml-1'>{fieldErrors.confirm_password}</p>}
                                    </div>

                                    {fieldErrors.token && (
                                        <div className='p-4 bg-red-50 rounded-xl border border-red-100'>
                                            <p className='text-red-700 text-sm'>{fieldErrors.token}</p>
                                        </div>
                                    )}

                                    <button
                                        type='submit'
                                        disabled={loading}
                                        className='mt-4 py-4 bg-[#012E80] hover:bg-[#011a4d] text-white text-lg font-bold rounded-xl transition-all duration-300 transform active:scale-[0.98] shadow-lg shadow-[#012E80]/20 flex justify-center items-center'
                                    >
                                        {loading ? (
                                            <div className='w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin'></div>
                                        ) : 'Reset Password'}
                                    </button>

                                    <div className='text-center mt-2'>
                                        <Link to='/auth-login' className='inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#012E80] transition-colors group'>
                                            <IconChevronLeft size={18} className='group-hover:-translate-x-1 transition-transform' />
                                            Back to Login
                                        </Link>
                                    </div>
                                </form>
                            </>
                        )}

                    </div>
                </div>

                <div className={`mt-auto pt-10 flex justify-center transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className='flex items-center gap-3 opacity-70 hover:opacity-100 transition-all duration-500 py-4 px-8 rounded-full border border-gray-200 hover:border-[#012E80]/20 hover:bg-[#012E80]/5 group cursor-default'>
                        <span className='text-[10px] uppercase tracking-[0.3em] font-medium text-gray-500'>Powered by</span>
                        <img src={hsclLogo2} alt='HSCL' className='h-6 w-auto object-contain brightness-95 group-hover:brightness-100' />
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; transform: scale(1); }
                    50% { opacity: 0.2; transform: scale(1.1); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite ease-in-out;
                }
            `}</style>
        </div>
    )
}
